import { useState } from 'react';
import { CheckOutlined } from '@mui/icons-material';
import { WEEKDAY_INITIALS } from '@practium/ui';
import { formatPeriodLabel } from '../../constants/maintenance.constants';
import styles from './MixtureDayRangePanel.module.css';

interface MixtureDayRangePanelProps {
	month: string;
	eventDays: string[];
	dateFrom: string;
	dateTo: string;
	onApply: (from: string, to: string) => void;
	onClear: () => void;
	onClose: () => void;
}

const monthDays = (month: string) => {
	const [year, monthNumber] = month.split(/[/-]/).map(Number);
	const prefix = `${year}-${String(monthNumber).padStart(2, '0')}`;
	const total = new Date(year, monthNumber, 0).getDate();
	return {
		offset: new Date(year, monthNumber - 1, 1).getDay(),
		days: Array.from(
			{ length: total },
			(_, index) => `${prefix}-${String(index + 1).padStart(2, '0')}`
		),
	};
};

const dayNumber = (day: string) => Number(day.slice(8));

export const MixtureDayRangePanel = ({
	month,
	eventDays,
	dateFrom,
	dateTo,
	onApply,
	onClear,
	onClose,
}: MixtureDayRangePanelProps) => {
	const [start, setStart] = useState(dateFrom);
	const [end, setEnd] = useState(dateTo);
	const [hoverDay, setHoverDay] = useState('');
	const { offset, days } = monthDays(month);
	const isPickingEnd = !!start && !end;
	const previewEnd = isPickingEnd && hoverDay >= start ? hoverDay : '';
	const rangeEnd = end || previewEnd || start;

	const selectDay = (day: string) => {
		if (!isPickingEnd || day < start) {
			setStart(day);
			setEnd('');
			return;
		}
		setEnd(day);
	};

	const dayClassName = (day: string) =>
		[
			styles.day,
			start && day >= start && day <= rangeEnd ? styles.inRange : '',
			day === start || day === end ? styles.edge : '',
			eventDays.includes(day) ? styles.hasEvent : '',
		]
			.filter(Boolean)
			.join(' ');

	const handleApply = () => {
		onApply(start, end || start);
		onClose();
	};

	const handleClear = () => {
		setStart('');
		setEnd('');
		onClear();
	};

	return (
		<>
			<div className={styles.header}>
				<span className={styles.month}>{formatPeriodLabel(month)}</span>
				<span className={styles.year}>{month.slice(0, 4)}</span>
			</div>
			<div className={styles.grid} onMouseLeave={() => setHoverDay('')}>
				{WEEKDAY_INITIALS.map((initial, index) => (
					<span key={`${initial}-${index}`} className={styles.weekday}>
						{initial}
					</span>
				))}
				{Array.from({ length: offset }, (_, index) => (
					<span key={`empty-${index}`} />
				))}
				{days.map((day) => (
					<button
						key={day}
						type="button"
						className={dayClassName(day)}
						onClick={() => selectDay(day)}
						onMouseEnter={() => setHoverDay(day)}
						aria-pressed={day === start || day === end}
					>
						{dayNumber(day)}
					</button>
				))}
			</div>
			<p className={styles.hint}>
				<span className={styles.hintDot} />
				<span>Dias com mistura prevista</span>
			</p>
			<button type="button" className={styles.applyButton} onClick={handleApply} disabled={!start}>
				<CheckOutlined className={styles.applyIcon} />
				Aplicar
			</button>
			<button
				type="button"
				className={styles.clearButton}
				onClick={handleClear}
				disabled={!start && !dateFrom}
			>
				Limpar
			</button>
		</>
	);
};
